import { useEffect, useState, useMemo, useCallback } from "react";
import { buildRowCount } from "@/useCases/rowCount";
import { type ColumnDef } from "@tanstack/react-table";
import { Card } from "@/ui/atoms/Card";
import { Loader, Modal, Button, DialogFooter, Pagination } from "@/ui/atoms";
import { DataTable } from "@/ui/molecules";
// Direct import (not the organisms barrel) keeps this view's dependency graph small.
import { FeatureFlagsModal, type TUserFeatureFlagsData } from "@/ui/organisms/FeatureFlagsModal";
import { Users, Plus, Edit2, Trash2, Check, X, Shield, Building2, Flag } from "lucide-react";
import { formatDateTime, EMPTY_ARRAY } from "@/utils/utils";
import type { TAsyncState } from "@/store";
import { useServerTable, type TServerTableQueryInput } from "@/useCases/useServerTable";
import { booleanParam, selectParam, sortParam, textParam } from "@/useCases/serverTableQuery";

export type TUser = {
    id: string;
    companyId: string;
    companyName: string;
    companyCode: string;
    email: string;
    displayName: string;
    role: string;
    subgroup: string | null;
    roleLabel?: string;
    subgroupLabel?: string | null;
    isActive: boolean;
    lastLoginAt: string | null;
    createdAt: string;
    updatedAt: string;
};

export type TCompany = {
    id: string;
    name: string;
    code: string;
};

export type TViewUsersParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useUsersState: () => TAsyncState<TUser[]>;
        usePagination: () => { page: number; pageSize: number; total: number };
        onPageChange: (page: number) => void;
        loadUsers: (
            query: {
                page: number;
                page_size: number;
                search?: string;
                is_active?: boolean;
                company_id?: string;
                sort_by?: string;
                sort_dir?: string;
            },
            signal?: AbortSignal,
        ) => void;
        useUsersTotalUnfiltered: () => number;
        useAvailableCompanies: () => TCompany[]; // Empty when not a Proda user or in view-as mode
        getCurrentUserId: () => string | null;
        getEffectiveCompanyId: () => string | null; // Current effective company (view-as or user's own)
        isProdaUser: () => boolean; // Current user belongs to Proda company
        isProdaAdmin: () => boolean; // Current user is Proda admin (can change companies)
        isViewingAsOtherCompany: () => boolean; // User is using view-as to view another company
        canAssignSubgroup: () => boolean; // Current user has user_assign_subgroup permission
        hasUserCreatePermission: () => boolean; // Current user has user_create
        hasUserEditPermission: () => boolean; // Current user has user_edit
        hasUserDeletePermission: () => boolean; // Current user has user_delete
        // Role/subgroup labels + partition sourced from /auth/me metadata.
        getRoleMetadata: () => {
            roleOptions: string[];
            roleLabels: Record<string, string>;
            subgroupLabels: Record<string, string>;
            roleSubgroups: Record<string, string[]>;
            roleDefaultSubgroup: Record<string, string>;
            assignableSubgroups: string[];
        };
        onCreateUser: (data: {
            companyId: string;
            email: string;
            displayName: string;
            role: string;
            subgroup?: string | null;
            isActive?: boolean;
        }) => Promise<void>;
        onUpdateUser: (
            id: string,
            data: {
                companyId?: string;
                displayName?: string;
                role?: string;
                subgroup?: string;
                isActive?: boolean;
            },
        ) => Promise<void>;
        onDeleteUser: (id: string) => Promise<void>;
        // Per-user feature flags. Only called when isProdaUser() — the
        // backend refuses everyone else and the UI never shows the button.
        loadUserFeatureFlags: (id: string) => Promise<TUserFeatureFlagsData>;
        onUpdateUserFeatureFlags: (
            id: string,
            overrides: Record<string, boolean | null>,
        ) => Promise<void>;
    };
};

export type TViewUsersProps = Record<string, never>;

// Labels come from /auth/me metadata; the per-row role_label/subgroup_label
// (from the API) is preferred, falling back to the raw value.
function getRoleLabel(role: string, roleLabel?: string): string {
    return roleLabel || role;
}

function getSubgroupLabel(subgroup: string | null, subgroupLabel?: string | null): string {
    if (!subgroup) return "—";
    return subgroupLabel || subgroup;
}

export const createViewUsers = ({ useCases }: TViewUsersParams) => {
    return function ViewUsers() {
        const {
            onMount,
            onUnmount,
            useUsersState,
            usePagination,
            loadUsers,
            useUsersTotalUnfiltered,
            useAvailableCompanies,
            getCurrentUserId,
            getEffectiveCompanyId,
            isProdaUser,
            isProdaAdmin,
            isViewingAsOtherCompany,
            canAssignSubgroup,
            hasUserCreatePermission,
            hasUserEditPermission,
            hasUserDeletePermission,
            getRoleMetadata,
            onCreateUser,
            onUpdateUser,
            onDeleteUser,
            loadUserFeatureFlags,
            onUpdateUserFeatureFlags,
        } = useCases();

        const { roleOptions, roleLabels, subgroupLabels, roleSubgroups, assignableSubgroups } =
            getRoleMetadata();
        const canCreate = hasUserCreatePermission();
        const canEdit = hasUserEditPermission();
        const canDeletePermission = hasUserDeletePermission();
        // Add User starts with no role/subgroup selected (both required).
        const EMPTY_ROLE = "";
        const EMPTY_SUBGROUP = "";

        const pagination = usePagination();
        const usersTotalUnfiltered = useUsersTotalUnfiltered();

        const usersToQuery = useCallback(
            (input: TServerTableQueryInput) => ({
                page: input.page,
                page_size: pagination.pageSize,
                search: textParam(input.columnFilters, "displayName"),
                is_active: booleanParam(input.columnFilters, "isActive"),
                company_id: selectParam(input.columnFilters, "companyId"),
                ...sortParam(input.sorting),
            }),
            [pagination.pageSize],
        );

        const server = useServerTable({
            pageSize: pagination.pageSize,
            toQuery: usersToQuery,
            fetchData: loadUsers,
        });
        // Stable; used to refresh the list after a mutation (preserves the
        // active search/filter/sort/page).
        const { refetch } = server;

        const usersState = useUsersState();
        const availableCompanies = useAvailableCompanies(); // Empty when not a Proda user or in view-as mode
        const currentUserId = getCurrentUserId();
        const effectiveCompanyId = getEffectiveCompanyId();
        const isInViewAsMode = isViewingAsOtherCompany();
        const showCompanyInfo = isProdaUser() && !isInViewAsMode; // Only Proda users see company info (not in view-as mode)
        const canChangeCompany = isProdaAdmin() && !isInViewAsMode; // Only Proda admins can change companies (not in view-as mode)
        const hasCompanySelection = availableCompanies.length > 0; // Whether to show company selection in forms
        const hasSubgroupPermission = canAssignSubgroup(); // Whether role/subgroup dropdowns are enabled
        // A user can only manage someone whose current subgroup is strictly below
        // their own (i.e. one they could assign). Unassigned users are manageable.
        const canManageSubject = (subgroup: string | null) =>
            subgroup === null || assignableSubgroups.includes(subgroup);

        const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
        const [isEditModalOpen, setIsEditModalOpen] = useState(false);
        const [editingUser, setEditingUser] = useState<TUser | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [error, setError] = useState<string | null>(null);

        // Activate/deactivate confirmation. The user pending a status change is
        // held here; a non-null value drives the confirmation modal. `toggleError`
        // is kept separate from the create/edit `error` so a failed toggle can't
        // leak into those forms.
        const [togglingUser, setTogglingUser] = useState<TUser | null>(null);
        const [isTogglingActive, setIsTogglingActive] = useState(false);
        const [toggleError, setToggleError] = useState<string | null>(null);

        // Create form state
        const [createFormData, setCreateFormData] = useState({
            companyId: "",
            email: "",
            displayName: "",
            role: EMPTY_ROLE,
            subgroup: EMPTY_SUBGROUP,
        });

        // Edit form state
        const [editFormData, setEditFormData] = useState({
            companyId: "",
            displayName: "",
            role: EMPTY_ROLE,
            subgroup: EMPTY_SUBGROUP,
        });

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        // Check if user belongs to Proda company (based on company code, not email)
        const isProdaCompany = useCallback(
            (user: TUser) => user.companyCode.toUpperCase() === "PRODA",
            [],
        );

        // Check if user can be deleted
        // Cannot delete: current user, Proda admin users
        const canDeleteUser = useCallback(
            (user: TUser) => {
                // Cannot delete yourself
                if (currentUserId && user.id === currentUserId) {
                    return false;
                }
                // Cannot delete Proda admin users (based on company and role)
                if (user.role === "admin" && isProdaCompany(user)) {
                    return false;
                }
                return true;
            },
            [currentUserId, isProdaCompany],
        );

        const getDeleteDisabledReason = useCallback(
            (user: TUser): string | null => {
                if (currentUserId && user.id === currentUserId) {
                    return "Cannot delete your own account";
                }
                if (user.role === "admin" && isProdaCompany(user)) {
                    return "Cannot delete Proda admin users";
                }
                return null;
            },
            [currentUserId, isProdaCompany],
        );

        // Check if company can be changed for a user
        const canChangeCompanyForUser = (user: TUser) => {
            // Cannot change your own company
            return canChangeCompany && currentUserId !== user.id;
        };

        // Generate company filter options from available companies
        const companyFilterOptions = useMemo(
            () => availableCompanies.map((t) => ({ value: t.id, label: `${t.name} (${t.code})` })),
            [availableCompanies],
        );

        // Handler callbacks (defined before columns useMemo to avoid hoisting issues)
        // Feature-flags modal (Proda users only)
        const canManageFlags = isProdaUser();
        const [flagsUser, setFlagsUser] = useState<TUser | null>(null);
        const [flagsData, setFlagsData] = useState<TUserFeatureFlagsData | null>(null);
        const [flagsError, setFlagsError] = useState<string | null>(null);
        const openFlagsModal = useCallback(
            (user: TUser) => {
                setFlagsUser(user);
                setFlagsData(null);
                setFlagsError(null);
                loadUserFeatureFlags(user.id)
                    .then(setFlagsData)
                    .catch((err: unknown) =>
                        setFlagsError(
                            err instanceof Error ? err.message : "Failed to load feature flags",
                        ),
                    );
            },
            [loadUserFeatureFlags],
        );
        const saveFlags = useCallback(
            async (overrides: Record<string, boolean | null>) => {
                if (!flagsUser) return;
                await onUpdateUserFeatureFlags(flagsUser.id, overrides);
            },
            [flagsUser, onUpdateUserFeatureFlags],
        );

        const openEditModal = useCallback((user: TUser) => {
            setEditFormData({
                companyId: user.companyId,
                displayName: user.displayName,
                role: user.role,
                subgroup: user.subgroup || "",
            });
            setEditingUser(user);
            setError(null);
            setIsEditModalOpen(true);
        }, []);

        // Clicking the status badge no longer toggles immediately — it opens a
        // confirmation modal that spells out what activating/deactivating does.
        const openToggleConfirm = useCallback((user: TUser) => {
            setToggleError(null);
            setTogglingUser(user);
        }, []);

        const confirmToggleActive = useCallback(async () => {
            if (!togglingUser) return;
            setIsTogglingActive(true);
            setToggleError(null);
            try {
                await onUpdateUser(togglingUser.id, { isActive: !togglingUser.isActive });
                setTogglingUser(null);
                refetch();
            } catch (err) {
                setToggleError(err instanceof Error ? err.message : "Failed to update user status");
            } finally {
                setIsTogglingActive(false);
            }
        }, [togglingUser, onUpdateUser, refetch]);

        const handleDelete = useCallback(
            async (user: TUser) => {
                if (
                    !confirm(
                        `Are you sure you want to delete "${user.displayName}"? This action cannot be undone.`,
                    )
                ) {
                    return;
                }
                try {
                    await onDeleteUser(user.id);
                    refetch();
                } catch (err) {
                    alert(err instanceof Error ? err.message : "Failed to delete user");
                }
            },
            [onDeleteUser, refetch],
        );

        // Define table columns
        const columns: ColumnDef<TUser>[] = useMemo(
            () => [
                {
                    accessorKey: "displayName",
                    header: "User",
                    cell: ({ row }) => {
                        const user = row.original;
                        return (
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold text-sm flex-shrink-0">
                                    {user.displayName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-medium">{user.displayName}</p>
                                        {currentUserId && user.id === currentUserId && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-600 font-medium">
                                                You
                                            </span>
                                        )}
                                        {user.role === "admin" && (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700 font-medium">
                                                <Shield className="h-3 w-3" />
                                                Admin
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">{user.email}</p>
                                </div>
                            </div>
                        );
                    },
                    meta: {
                        filterType: "text",
                        filterPlaceholder: "Search users...",
                    },
                    enableSorting: true,
                },
                // Only show Company column when Proda user has access to multiple companies
                ...(showCompanyInfo && availableCompanies.length > 1
                    ? [
                          {
                              accessorKey: "companyId",
                              header: "Company",
                              cell: ({ row }: { row: { original: TUser } }) => {
                                  const user = row.original;
                                  return (
                                      <div className="flex items-center gap-1">
                                          <Building2 className="h-3 w-3 text-muted-foreground" />
                                          <span className="text-sm">{user.companyName}</span>
                                          <span className="text-xs text-muted-foreground">
                                              ({user.companyCode})
                                          </span>
                                      </div>
                                  );
                              },
                              meta: {
                                  filterType: "select" as const,
                                  filterOptions: companyFilterOptions,
                                  filterPlaceholder: "All companies",
                              },
                          } as ColumnDef<TUser>,
                      ]
                    : []),
                {
                    accessorKey: "role",
                    header: "Role",
                    cell: ({ row }) => {
                        const user = row.original;
                        return (
                            <div className="flex flex-wrap gap-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted font-medium">
                                    {getRoleLabel(user.role, user.roleLabel)}
                                </span>
                                {user.subgroup && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600 font-medium">
                                        {getSubgroupLabel(user.subgroup, user.subgroupLabel)}
                                    </span>
                                )}
                            </div>
                        );
                    },
                    enableSorting: false,
                },
                {
                    accessorKey: "lastLoginAt",
                    header: "Last Login",
                    cell: ({ row }) => (
                        <span className="text-sm text-muted-foreground">
                            {row.original.lastLoginAt
                                ? formatDateTime(row.original.lastLoginAt)
                                : "Never"}
                        </span>
                    ),
                },
                {
                    accessorKey: "isActive",
                    header: "Status",
                    cell: ({ row }) => {
                        const user = row.original;
                        const isCurrentUser = currentUserId === user.id;
                        const baseClass =
                            "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium";
                        const statusContent = user.isActive ? (
                            <>
                                <Check className="h-3 w-3" />
                                Active
                            </>
                        ) : (
                            <>
                                <X className="h-3 w-3" />
                                Inactive
                            </>
                        );
                        // Toggling status calls PATCH is_active, gated by USER_EDIT
                        // on the backend. Without that permission, show the status
                        // as a read-only badge (visible to anyone who can view users).
                        if (!canEdit) {
                            return (
                                <span
                                    className={`${baseClass} ${
                                        user.isActive
                                            ? "bg-emerald-100 text-emerald-700"
                                            : "bg-slate-100 text-slate-600"
                                    }`}
                                >
                                    {statusContent}
                                </span>
                            );
                        }
                        // Can't deactivate yourself (but can view status)
                        const canToggle = !isCurrentUser || !user.isActive;
                        return (
                            <button
                                data-testid="user-active-toggle"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (canToggle) openToggleConfirm(user);
                                }}
                                disabled={isCurrentUser && user.isActive}
                                className={`${baseClass} transition-colors ${
                                    user.isActive
                                        ? isCurrentUser
                                            ? "bg-emerald-100 text-emerald-700 cursor-not-allowed"
                                            : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                                title={
                                    isCurrentUser && user.isActive
                                        ? "Cannot deactivate your own account"
                                        : user.isActive
                                          ? "Deactivate this user"
                                          : "Activate this user"
                                }
                            >
                                {statusContent}
                            </button>
                        );
                    },
                    meta: {
                        filterType: "boolean",
                        filterPlaceholder: "All statuses",
                    },
                },
                {
                    id: "actions",
                    header: "",
                    cell: ({ row }) => {
                        const user = row.original;
                        return (
                            <div className="flex items-center justify-end gap-1">
                                {canEdit && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openEditModal(user);
                                        }}
                                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                                        title="Edit"
                                    >
                                        <Edit2 className="h-4 w-4" />
                                    </button>
                                )}
                                {canManageFlags && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openFlagsModal(user);
                                        }}
                                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                                        title="Feature flags"
                                        data-testid={`user-flags-${user.email}`}
                                    >
                                        <Flag className="h-4 w-4" />
                                    </button>
                                )}
                                {canDeletePermission && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(user);
                                        }}
                                        disabled={!canDeleteUser(user)}
                                        className={`p-2 rounded-lg transition-colors ${
                                            canDeleteUser(user)
                                                ? "text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                : "text-muted-foreground/30 cursor-not-allowed"
                                        }`}
                                        title={getDeleteDisabledReason(user) || "Delete"}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        );
                    },
                    enableSorting: false,
                },
            ],
            [
                currentUserId,
                showCompanyInfo,
                availableCompanies.length,
                companyFilterOptions,
                canDeleteUser,
                canDeletePermission,
                canEdit,
                getDeleteDisabledReason,
                handleDelete,
                openToggleConfirm,
                openEditModal,
                canManageFlags,
                openFlagsModal,
            ],
        );

        const resetCreateForm = () => {
            setCreateFormData({
                // Use first available company if selection exists, otherwise use effective company
                companyId: availableCompanies[0]?.id || effectiveCompanyId || "",
                email: "",
                displayName: "",
                role: EMPTY_ROLE,
                subgroup: EMPTY_SUBGROUP,
            });
            setError(null);
            setIsCreateModalOpen(false);
        };

        const resetEditForm = () => {
            setEditFormData({
                companyId: "",
                displayName: "",
                role: EMPTY_ROLE,
                subgroup: EMPTY_SUBGROUP,
            });
            setEditingUser(null);
            setError(null);
            setIsEditModalOpen(false);
        };

        const openCreateModal = () => {
            setCreateFormData({
                // Use first available company if selection exists, otherwise use effective company
                companyId: availableCompanies[0]?.id || effectiveCompanyId || "",
                email: "",
                displayName: "",
                role: EMPTY_ROLE,
                subgroup: EMPTY_SUBGROUP,
            });
            setError(null);
            setIsCreateModalOpen(true);
        };

        const handleCreateSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setError(null);
            setIsSubmitting(true);
            try {
                await onCreateUser({
                    companyId: createFormData.companyId,
                    email: createFormData.email,
                    displayName: createFormData.displayName,
                    role: createFormData.role,
                    subgroup: createFormData.subgroup || undefined,
                });
                // New user may not belong on the current page; reset to page 1.
                server.setPage(1);
                refetch();
                resetCreateForm();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to create user");
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleEditSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            if (!editingUser) return;

            setError(null);
            setIsSubmitting(true);
            try {
                // Only send role/subgroup when they actually changed. The backend
                // gates any present role/subgroup field on USER_ASSIGN_SUBGROUP, so
                // sending them unchanged would 403 an admin who only has USER_EDIT.
                const roleChanged = editFormData.role !== editingUser.role;
                const newSubgroup = editFormData.subgroup || null;
                const subgroupChanged = newSubgroup !== (editingUser.subgroup ?? null);
                await onUpdateUser(editingUser.id, {
                    // Only include companyId if it changed
                    companyId:
                        editFormData.companyId !== editingUser.companyId
                            ? editFormData.companyId
                            : undefined,
                    displayName: editFormData.displayName,
                    role: roleChanged ? editFormData.role : undefined,
                    subgroup: subgroupChanged ? editFormData.subgroup || undefined : undefined,
                });
                refetch();
                resetEditForm();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to update user");
            } finally {
                setIsSubmitting(false);
            }
        };

        const renderRoleSubgroupFields = (
            formData: { role: string; subgroup: string },
            setFormData: (updater: (prev: typeof formData) => typeof formData) => void,
            idPrefix: string,
            locked = false,
        ) => {
            // Dropdowns are disabled when the caller can't assign at all, or is
            // editing a user at/above their own level (locked).
            const fieldsDisabled = !hasSubgroupPermission || locked;
            const isAssignable = (sg: string) => assignableSubgroups.includes(sg);
            // First assignable subgroup for a role — picked when the role changes.
            const firstAssignableForRole = (role: string) =>
                (roleSubgroups[role] ?? []).find(isAssignable) ?? "";
            // Only offer roles that have an assignable subgroup; keep the current
            // role so an edited user's existing role still renders.
            const availableRoles = roleOptions.filter(
                (role) => (roleSubgroups[role] ?? []).some(isAssignable) || role === formData.role,
            );
            // Only offer assignable subgroups for the selected role; keep the
            // current value so an edited user's existing subgroup still renders.
            const subgroupOptions = (roleSubgroups[formData.role] ?? []).filter(
                (sg) => isAssignable(sg) || sg === formData.subgroup,
            );
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label
                            className="block text-sm font-medium mb-1"
                            htmlFor={`${idPrefix}-role`}
                        >
                            Role <span className="text-destructive">*</span>
                        </label>
                        <select
                            id={`${idPrefix}-role`}
                            data-testid={idPrefix === "create" ? "user-role-select" : undefined}
                            value={formData.role}
                            onChange={(e) => {
                                const newRole = e.target.value;
                                setFormData((prev) => ({
                                    ...prev,
                                    role: newRole,
                                    subgroup: newRole ? firstAssignableForRole(newRole) : "",
                                }));
                            }}
                            disabled={fieldsDisabled}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                fieldsDisabled
                                    ? "bg-muted text-muted-foreground cursor-not-allowed"
                                    : "bg-background"
                            }`}
                            required
                        >
                            {formData.role === "" && (
                                <option value="" disabled>
                                    Select a role…
                                </option>
                            )}
                            {availableRoles.map((role) => (
                                <option key={role} value={role}>
                                    {roleLabels[role] ?? role}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label
                            className="block text-sm font-medium mb-1"
                            htmlFor={`${idPrefix}-subgroup`}
                        >
                            Subgroup <span className="text-destructive">*</span>
                        </label>
                        <select
                            id={`${idPrefix}-subgroup`}
                            value={formData.subgroup}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, subgroup: e.target.value }))
                            }
                            disabled={fieldsDisabled || formData.role === ""}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                !fieldsDisabled && formData.role !== ""
                                    ? "bg-background"
                                    : "bg-muted text-muted-foreground cursor-not-allowed"
                            }`}
                            required
                        >
                            {formData.subgroup === "" && (
                                <option value="" disabled>
                                    Select a subgroup…
                                </option>
                            )}
                            {subgroupOptions.map((sg) => (
                                <option key={sg} value={sg}>
                                    {subgroupLabels[sg] ?? sg}
                                </option>
                            ))}
                        </select>
                        {!hasSubgroupPermission && (
                            <p className="text-xs text-muted-foreground mt-1">
                                You do not have permission to change role or subgroup
                            </p>
                        )}
                        {hasSubgroupPermission && locked && (
                            <p className="text-xs text-muted-foreground mt-1">
                                You cannot change the role or subgroup of a user at or above your
                                own level
                            </p>
                        )}
                    </div>
                </div>
            );
        };

        const renderCreateForm = () => {
            // Disable submit until every required field is provided.
            const isCreateFormValid =
                createFormData.email.trim() !== "" &&
                createFormData.displayName.trim() !== "" &&
                createFormData.role !== "" &&
                createFormData.subgroup !== "" &&
                (!hasCompanySelection || createFormData.companyId !== "");

            return (
                <form onSubmit={handleCreateSubmit} className="space-y-4">
                    {error && (
                        <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Only show company dropdown when company selection is available (Proda users not in view-as mode) */}
                        {hasCompanySelection && (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">
                                    Company <span className="text-destructive">*</span>
                                </label>
                                <select
                                    value={createFormData.companyId}
                                    onChange={(e) =>
                                        setCreateFormData((prev) => ({
                                            ...prev,
                                            companyId: e.target.value,
                                        }))
                                    }
                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                    required
                                >
                                    <option value="">Select a company...</option>
                                    {availableCompanies.map((company) => (
                                        <option key={company.id} value={company.id}>
                                            {company.name} ({company.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Email <span className="text-destructive">*</span>
                            </label>
                            <input
                                data-testid="user-email-input"
                                type="email"
                                value={createFormData.email}
                                onChange={(e) =>
                                    setCreateFormData((prev) => ({
                                        ...prev,
                                        email: e.target.value,
                                    }))
                                }
                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="name@company.com"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Display Name <span className="text-destructive">*</span>
                            </label>
                            <input
                                data-testid="user-name-input"
                                type="text"
                                value={createFormData.displayName}
                                onChange={(e) =>
                                    setCreateFormData((prev) => ({
                                        ...prev,
                                        displayName: e.target.value,
                                    }))
                                }
                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="John Doe"
                                required
                            />
                        </div>
                    </div>

                    {renderRoleSubgroupFields(
                        { role: createFormData.role, subgroup: createFormData.subgroup },
                        (updater) =>
                            setCreateFormData((prev) => ({
                                ...prev,
                                ...updater({ role: prev.role, subgroup: prev.subgroup }),
                            })),
                        "create",
                    )}

                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={resetCreateForm}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            data-testid="user-submit-button"
                            type="submit"
                            disabled={isSubmitting || !isCreateFormValid}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader size="sm" className="mr-2" />
                                    Creating...
                                </>
                            ) : (
                                "Create User"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            );
        };

        const renderEditForm = () => {
            const canEditCompany = editingUser && canChangeCompanyForUser(editingUser);

            return (
                <form onSubmit={handleEditSubmit} className="space-y-4">
                    {error && (
                        <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Only show company field to Proda users */}
                        {showCompanyInfo && (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">
                                    Company
                                    {canEditCompany && (
                                        <span className="text-destructive ml-1">*</span>
                                    )}
                                </label>
                                <select
                                    value={editFormData.companyId}
                                    onChange={(e) =>
                                        setEditFormData((prev) => ({
                                            ...prev,
                                            companyId: e.target.value,
                                        }))
                                    }
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                                        canEditCompany
                                            ? "bg-background"
                                            : "bg-muted text-muted-foreground cursor-not-allowed"
                                    }`}
                                    disabled={!canEditCompany}
                                    required
                                >
                                    {availableCompanies.map((company) => (
                                        <option key={company.id} value={company.id}>
                                            {company.name} ({company.code})
                                        </option>
                                    ))}
                                </select>
                                {!canEditCompany && editingUser?.id === currentUserId && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Cannot change your own company
                                    </p>
                                )}
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Display Name <span className="text-destructive">*</span>
                            </label>
                            <input
                                type="text"
                                value={editFormData.displayName}
                                onChange={(e) =>
                                    setEditFormData((prev) => ({
                                        ...prev,
                                        displayName: e.target.value,
                                    }))
                                }
                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                placeholder="John Doe"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Email</label>
                            <input
                                type="email"
                                value={editingUser?.email || ""}
                                className="w-full px-3 py-2 border rounded-lg bg-muted text-muted-foreground"
                                disabled
                            />
                        </div>
                    </div>

                    {renderRoleSubgroupFields(
                        { role: editFormData.role, subgroup: editFormData.subgroup },
                        (updater) =>
                            setEditFormData((prev) => ({
                                ...prev,
                                ...updater({ role: prev.role, subgroup: prev.subgroup }),
                            })),
                        "edit",
                        !!editingUser && !canManageSubject(editingUser.subgroup),
                    )}

                    <DialogFooter className="gap-2 pt-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={resetEditForm}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Loader size="sm" className="mr-2" />
                                    Saving...
                                </>
                            ) : (
                                "Save Changes"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            );
        };

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Users className="h-6 w-6" />
                            Users
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Manage system users and their permissions
                        </p>
                    </div>
                    {canCreate && (
                        <Button data-testid="user-create-button" onClick={openCreateModal}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add User
                        </Button>
                    )}
                </div>

                {/* Users list */}
                <Card className="overflow-hidden">
                    {usersState.status === "pending" && !usersState.data && (
                        <div className="flex items-center justify-center py-12">
                            <Loader size="lg" />
                        </div>
                    )}

                    {usersState.status === "rejected" && (
                        <div className="p-6 text-center">
                            <p className="text-destructive font-medium">Failed to load users</p>
                            <p className="text-muted-foreground text-sm mt-1">
                                {usersState.error?.message || "An unexpected error occurred"}
                            </p>
                        </div>
                    )}

                    {(usersState.status === "fulfilled" || usersState.data) && (
                        <DataTable
                            columns={columns}
                            storageKey="users"
                            data={usersState.data ?? EMPTY_ARRAY}
                            loading={usersState.status === "pending"}
                            emptyMessage="No users found."
                            manualFiltering
                            manualSorting
                            sortingState={server.sorting}
                            onSortingChange={server.onSortingChange}
                            onColumnFiltersChange={server.onColumnFiltersChange}
                            initialColumnFilters={server.columnFilters}
                            rowNoun={{ one: "user", many: "users" }}
                            rowCount={buildRowCount({
                                total: pagination.total,
                                totalUnfiltered: usersTotalUnfiltered,
                                hasActiveFilters: server.columnFilters.length > 0,
                            })}
                        />
                    )}
                    <Pagination
                        page={server.page}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        onPageChange={server.setPage}
                        className="px-6 py-4 border-t"
                    />
                </Card>

                {/* Feature flags modal (Proda users only) */}
                {canManageFlags && (
                    <FeatureFlagsModal
                        open={flagsUser !== null}
                        onClose={() => setFlagsUser(null)}
                        subjectLabel={flagsUser?.displayName || flagsUser?.email || ""}
                        data={flagsData}
                        loadError={flagsError}
                        onSave={saveFlags}
                    />
                )}

                {/* Create User Modal */}
                <Modal
                    open={isCreateModalOpen}
                    onOpenChange={(open) => !open && resetCreateForm()}
                    title="Add User"
                    description="Create a new user account. The user will be able to log in with this email via Auth0."
                >
                    {renderCreateForm()}
                </Modal>

                {/* Edit User Modal */}
                <Modal
                    open={isEditModalOpen}
                    onOpenChange={(open) => !open && resetEditForm()}
                    title="Edit User"
                    description="Update user details and permissions."
                >
                    {renderEditForm()}
                </Modal>

                {/* Activate / deactivate confirmation */}
                {togglingUser && (
                    <Modal
                        open
                        onOpenChange={(open) => {
                            if (!open && !isTogglingActive) setTogglingUser(null);
                        }}
                        title={togglingUser.isActive ? "Deactivate user" : "Activate user"}
                        description={
                            togglingUser.isActive
                                ? `Deactivate "${togglingUser.displayName}"?`
                                : `Activate "${togglingUser.displayName}"?`
                        }
                        className="max-w-md"
                    >
                        <div className="space-y-4">
                            {toggleError && (
                                <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                                    {toggleError}
                                </div>
                            )}
                            <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground space-y-2">
                                {togglingUser.isActive ? (
                                    <>
                                        <p>
                                            <span className="font-medium text-foreground">
                                                {togglingUser.email}
                                            </span>{" "}
                                            will immediately lose access — they will no longer be
                                            able to sign in or use PRODA Finance.
                                        </p>
                                        <p>
                                            Their account, role and history are kept, and you can
                                            reactivate them at any time.
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p>
                                            <span className="font-medium text-foreground">
                                                {togglingUser.email}
                                            </span>{" "}
                                            will regain access and be able to sign in to PRODA
                                            Finance again.
                                        </p>
                                        <p>Their previous role and permissions are restored.</p>
                                    </>
                                )}
                            </div>
                            <DialogFooter className="gap-2 pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setTogglingUser(null)}
                                    disabled={isTogglingActive}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    data-testid="user-active-confirm"
                                    type="button"
                                    variant={togglingUser.isActive ? "destructive" : "success"}
                                    onClick={confirmToggleActive}
                                    disabled={isTogglingActive}
                                >
                                    {isTogglingActive ? (
                                        <>
                                            <Loader size="sm" className="mr-2" />
                                            {togglingUser.isActive
                                                ? "Deactivating..."
                                                : "Activating..."}
                                        </>
                                    ) : togglingUser.isActive ? (
                                        "Deactivate user"
                                    ) : (
                                        "Activate user"
                                    )}
                                </Button>
                            </DialogFooter>
                        </div>
                    </Modal>
                )}
            </div>
        );
    };
};
